import {
  AppSessionCellId,
  CellId,
  CellReferencesV2,
  GraphNodeV3,
  HexVersionId,
  MonacoLanguageType,
  ParameterName,
  ParameterOutputType,
  SecretName,
  UPDATE_CODE_CELL,
  UPDATE_MARKDOWN_CELL,
  UPDATE_SQL_CELL,
  convertJinjaSqlReferences,
  mergeStoredCellReferencesV3,
  stableEmptyArray,
  stableEmptyObject,
  upgradeCellReferencesV2,
  uuid,
} from "@hex/common";
import { editor as Editor, IDisposable, Uri } from "monaco-editor";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { ScopeItemFragment } from "../../appsession-multiplayer/AppSessionMPModel.generated";
import { useKernelCompletion } from "../../components/cell/monaco/KernelCompletionProvider";
import { PARAMETER_COMPLETION_PROVIDER } from "../../components/cell/monaco/ParameterCompletionProvider";
import { useCellData } from "../../components/output/useCellData";
import { selectGraphNodeV3 } from "../../graph/graphNodeV3Hooks";
import { useCellContentsGetter } from "../../hex-version-multiplayer/state-hooks/cellContentsStateHooks";
import { useUpdateLock } from "../../hooks/locks/useLock";
import { useDebouncedCallback } from "../../hooks/useDebouncedCallback";
import { useShouldSuppressUndefinedReferences } from "../../hooks/useErroredCells";
import { RootState, useSelector, useStore } from "../../redux/hooks.js";
import { appSessionMPSelectors } from "../../redux/slices/appSessionMPSlice";
import {
  CellContentsMP,
  CellMP,
  hexVersionMPSelectors,
} from "../../redux/slices/hexVersionMPSlice";
import { selectProjectSearchState } from "../../redux/slices/logicViewSlice.js";
import {
  createSelectorV2,
  createShallowResultSelectorV2,
} from "../../redux/utils/index.js";
import { useHexVersionAOContext } from "../../util/hexVersionAOContext";
import { useHexFlag } from "../../util/useHexFlags";
import { useProjectContext } from "../../util/projectContext";
import { useSessionContext } from "../../util/sessionContext";

import { editHistoryManager } from "./editHistoryTracker";
import { HighlightContext, updateHighlights } from "./updateHighlights";

// Non-serializable state shouldn't go in Redux, so keeps models outside of it.
const modelsByCellId: Record<string, Editor.ITextModel | undefined> = {};
const cellIdsByModelURI: Record<string, CellId | undefined> = {};
const highlightsByCellId: Record<string, string[] | undefined> = {};
const lastSavedSourceByCellId: Record<string, string> = {};
// used to figure out if we need to GC a given model
const numberOfWatchersByCellId: Record<string, number | undefined> = {};

export const getModel = (cellId: CellId): Editor.ITextModel | undefined => {
  return modelsByCellId[cellId];
};

export const getCellIdForModel = (
  model: Editor.ITextModel,
): CellId | undefined => cellIdsByModelURI[model.uri.toString()];

export const getOrCreateModel = (cellId: CellId): Editor.ITextModel => {
  let model = modelsByCellId[cellId];
  if (model == null) {
    model = Editor.createModel(
      "",
      // this will change for the model eventually,
      // but monaco runs a slow function to guess the language if we don't provide one here.
      "plaintext",
      // make sure that the model gets a unique uuid that
      // is not an incrementing number
      Uri.from({ scheme: "inmemory", authority: "model", path: `/${uuid()}` }),
    );
    modelsByCellId[cellId] = model;
    cellIdsByModelURI[model.uri.toString()] = cellId;

    editHistoryManager.registerModel(model, cellId);
  }
  return model;
};

function useLastSavedSource(
  cellId: string,
  source: string,
): [string, (source: string) => void] {
  const isInitialized = useRef(false);

  const setLastSavedSource = useCallback(
    (lastSavedSource: string) => {
      lastSavedSourceByCellId[cellId] = lastSavedSource;
    },
    [cellId],
  );

  if (!isInitialized.current) {
    setLastSavedSource(source);
    isInitialized.current = true;
  }
  return [lastSavedSourceByCellId[cellId] ?? "", setLastSavedSource];
}

/**
 * This returns a monaco model with read only contents for a given cell.
 *
 * The model's contents will only be up to date with the source only if
 * `useWritableModel` is currently mounted somewhere else.
 */
export const useModel = (cellId: CellId): Editor.ITextModel => {
  const model = getOrCreateModel(cellId);

  // We increment the number of watchers in useLayoutEffect because it's called before useEffect.
  // If we need two models for the same cellId (ie a component that calls useModel is unmounted and remounted by its parent),
  // we need to increment the total count of usages of the model for the cellId before the garbage collection cleans up the
  // usage that is being unmounted. Otherwise, the newly-accessed instance of the model will be disposed and unusable.
  useLayoutEffect(() => {
    const mountNumberOfWatchers = numberOfWatchersByCellId[cellId] ?? 0;
    numberOfWatchersByCellId[cellId] = mountNumberOfWatchers + 1;
  }, [cellId]);

  useEffect(() => {
    return () => {
      const unmountNumberOfWatchers = numberOfWatchersByCellId[cellId] ?? 0;
      numberOfWatchersByCellId[cellId] = unmountNumberOfWatchers - 1;

      // garabage collect old models
      if (numberOfWatchersByCellId[cellId] === 0) {
        const modelToDispose = modelsByCellId[cellId];
        if (modelToDispose) {
          delete cellIdsByModelURI[modelToDispose.uri.toString()];
          editHistoryManager.unregisterModel(modelToDispose);
          modelToDispose.dispose();
        }
        delete modelsByCellId[cellId];
        delete numberOfWatchersByCellId[cellId];
      }
    };
  }, [cellId]);

  return model;
};

// If you change this value, be sure to test out a larger notebook
// to make sure there isn't any typing lag as a result
const UPDATE_CODE_DEBOUNCE = 500;

// this time is deliberately different than UPDATE_CODE_DEBOUNCE to avoid
// doing too much work at once.
const UPDATE_HIGHLIGHT_DELAY = 450;

export type useWritableModelResult = {
  model: Editor.ITextModel;
  updateHighlights: () => void;
};

/**
 * Returns the monaco model for a given cell with mutable content.
 * This hook should only be mounted _once_ per cell id at a given time.
 */
export const useWritableModel = ({
  appSessionCellId,
  cellId,
  language,
  source,
}: {
  cellId: CellId;
  appSessionCellId: AppSessionCellId | undefined;
  source: string;
  language: MonacoLanguageType;
}): useWritableModelResult => {
  const store = useStore();
  const { dispatchAO } = useHexVersionAOContext();

  const { hexVersionId } = useProjectContext();
  const { appSessionId } = useSessionContext();

  const { acquireLock } = useUpdateLock({
    canUnlock: false,
    cellId,
  });

  const { state: cellExecutionState } = useCellData({ appSessionCellId });

  const suppressUndefinedReferences = useShouldSuppressUndefinedReferences(
    cellId,
    cellExecutionState,
  );
  const pipWarnUseUv = useHexFlag("pip-warn-use-uv-in-editor");
  const getCellContents = useCellContentsGetter();

  const model = useModel(cellId);
  const setHighlights = useCallback(
    (cb: (prevHighlights: string[]) => string[]): void => {
      const prevHighlights = highlightsByCellId[cellId] || [];
      const newHighlights = cb(prevHighlights);
      highlightsByCellId[cellId] = newHighlights;
    },
    [cellId],
  );

  const [lastSavedSource, setLastSavedSource] = useLastSavedSource(
    cellId,
    source,
  );

  const getCompletionId = useCallback(
    (name: string) => {
      return `${cellId}-${name}`;
    },
    [cellId],
  );

  const newParams = useSelector((state) =>
    selectNewParams(state, hexVersionId, cellId),
  );

  useEffect(() => {
    const completionIds: string[] = [];
    const completionItems: Parameters<
      typeof PARAMETER_COMPLETION_PROVIDER.addCompletions
    >[0] = [];
    newParams.forEach((param) => {
      const completionId = getCompletionId(param);
      completionIds.push(completionId);
      completionItems.push({
        id: completionId,
        completion: {
          name: param,
          outputType: ParameterOutputType.DYNAMIC,
          // TODO: show preview of the line of code that defined the param
        },
      });
    });
    PARAMETER_COMPLETION_PROVIDER.addCompletions(completionItems);
    return () => {
      PARAMETER_COMPLETION_PROVIDER.removeCompletions(completionIds);
    };
  }, [getCompletionId, newParams]);

  const updateCellSource = useCallback(
    (newSource: string) => {
      setLastSavedSource(newSource);
      const contents = getCellContents(cellId);
      if (contents.__typename === "CodeCell") {
        dispatchAO(
          UPDATE_CODE_CELL.create({
            codeCellId: contents.codeCellId,
            cellId,
            key: "source",
            value: newSource,
          }),
          { skipUndoBuffer: true },
        );
      } else if (contents.__typename === "MarkdownCell") {
        dispatchAO(
          UPDATE_MARKDOWN_CELL.create({
            markdownCellId: contents.markdownCellId,
            cellId,
            key: "source",
            value: newSource,
          }),
          { skipUndoBuffer: true },
        );
      } else if (contents.__typename === "SqlCell") {
        dispatchAO(
          UPDATE_SQL_CELL.create({
            sqlCellId: contents.sqlCellId,
            cellId,
            key: "source",
            value: newSource,
          }),
          { skipUndoBuffer: true },
        );
      }
    },
    [cellId, dispatchAO, getCellContents, setLastSavedSource],
  );

  const throttledAcquireLock = useDebouncedCallback(acquireLock, 30 * 1000, {
    leading: true,
    maxWait: 30 * 1000,
  });

  const debouncedUpdateCell = useDebouncedCallback(
    updateCellSource,
    UPDATE_CODE_DEBOUNCE,
  );

  const kernelCompletion = useKernelCompletion();

  const updateHighlightsCallback = useCallback((): void => {
    const state = store.getState();
    const cellReferencesArray = selectCellReferences(
      state,
      hexVersionId,
      cellId,
    );
    const graphNode = selectGraphNodeV3(state, hexVersionId, cellId);
    const refScopes = selectRefScopes(
      state,
      hexVersionId,
      cellId,
      appSessionId,
    );
    const context = selectContext(state, hexVersionId, cellId);
    const projectSearchState = selectProjectSearchState(state);

    if (model != null && graphNode != null) {
      setHighlights((prevHighlights) =>
        updateHighlights({
          kernelCompletion,
          cellReferencesArray: cellReferencesArray.map((c) =>
            upgradeCellReferencesV2(c, null),
          ),
          graphNode: graphNode,
          refScopes,
          model,
          language,
          oldHighlights: prevHighlights,
          context,
          pipWarnUseUv,
          suppressUndefinedReferences,
          projectSearchState,
          cellId,
        }),
      );
    }
  }, [
    store,
    hexVersionId,
    cellId,
    appSessionId,
    model,
    setHighlights,
    kernelCompletion,
    language,
    pipWarnUseUv,
    suppressUndefinedReferences,
  ]);

  const debouncedUpdateHighlightsCallback = useDebouncedCallback(
    updateHighlightsCallback,
    UPDATE_HIGHLIGHT_DELAY,
  );

  useEffect(() => {
    Editor.setModelLanguage(model, language);
    updateHighlightsCallback();
  }, [language, model, updateHighlightsCallback]);

  // rehighlight whenever search state changes
  const projectSearchState = useSelector(selectProjectSearchState);
  useEffect(() => {
    debouncedUpdateHighlightsCallback();
  }, [debouncedUpdateHighlightsCallback, projectSearchState]);

  const modelListenerFn = useCallback(
    ({ eol }: Editor.IModelContentChangedEvent) => {
      throttledAcquireLock();
      // R kernels have issues parsing windows style EOL (CRLF) so we force LF
      // unfortunately, there is no way to configure this change permanently for a model
      // so we have to check after everychange to see if monaco has auto changed EOL to CRLF
      // https://github.com/microsoft/monaco-editor/issues/2019
      if (eol === "\r\n") {
        // we use set instead of push so that this change does not end up on monaco's
        // undo stack and cause head aches.
        model.setEOL(Editor.EndOfLineSequence.LF);
      }
      debouncedUpdateCell(model.getValue());
      debouncedUpdateHighlightsCallback();
    },
    [
      debouncedUpdateCell,
      debouncedUpdateHighlightsCallback,
      model,
      throttledAcquireLock,
    ],
  );

  // Track the disposable of the onDidChangeContent listener so we can dispose of it when the component unmounts
  const didChangeContentRef = useRef<IDisposable | null>(null);

  useEffect(() => {
    return () => {
      // Always clear the reference to the listener when the component unmounts
      didChangeContentRef.current?.dispose();
      didChangeContentRef.current = null;
    };
  }, []);

  // Every time the model changes, swap the old listener for a new one, disposing the old one.
  useEffect(() => {
    const oldListener = didChangeContentRef.current;
    const newListener = model.onDidChangeContent(modelListenerFn);
    if (oldListener != null) {
      oldListener.dispose();
    }

    didChangeContentRef.current = newListener;
  }, [modelListenerFn, model]);

  const setModelValue = useCallback(
    (newValue: string, silent?: boolean) => {
      // Prevents the same cell, mounted twice, from triggering update AOs.
      //
      // One example of this bug was when opening the Version History dialog caused
      // any onscreen monaco-based cells (e.g. SQL, markdown) to trigger update AOs:
      //
      // https://hex-tech-hq.slack.com/archives/CQ1LCD22K/p1768332355345599
      //
      // This was because two different instances of the cell were calling
      // useWritableModel, even though the one in the dialog was read-only.
      // For example, if there's a project with one SQL cell, opening the dialog
      // opens a copy of the notebook, showing a (versionEditable false) readonly
      // copy of the SQL cell. Despite being readonly, the second, foreground copy
      // of the cell called useWritableModel, which triggered the useEffect below
      // this callback, which caused update logic to run even though the user didn't
      // do anything and the cell source was unchanged.
      //
      // This would also happen if Hex implemented a split-screen feature, where you
      // can edit two copies of the same project side by side.
      if (newValue === model.getValue()) {
        return;
      }
      if (model.isDisposed()) {
        // TODO: should we log
        return;
      }
      if (silent) {
        // we need to dispose of the listener before we set the value, so we don't trigger it
        if (didChangeContentRef.current != null) {
          didChangeContentRef.current.dispose();
        }
        model.setValue(newValue);
        updateHighlightsCallback();
        didChangeContentRef.current = model.onDidChangeContent(modelListenerFn);
      } else {
        model.setValue(newValue);
      }
    },
    [updateHighlightsCallback, model, modelListenerFn],
  );

  // Initialzes what the source should be for a cell
  useEffect(() => {
    setModelValue(source, true);
    // we DO NOT want this effect to fire whenever source changes
    // ONLY whenever our model changes (e.g. language changes)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  // keep the model in sync with the upstream source value
  // but only if it does not match what we expect
  useEffect(() => {
    if (lastSavedSource !== source) {
      setModelValue(source, true);
      // display updates that originate from outside the monaco editor
      // (i.e. server corrections)
      setLastSavedSource(source);
    }
    // we DO NOT want this effect to fire whenever last saved source changes
    // ONLY when source changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, setModelValue]);

  useEffect(() => {
    // Check for tokenization and do intial highlight
    // gross hack because of https://github.com/microsoft/monaco-editor/issues/115
    // Wait until tokenization is ready
    let waitedSoFar = 0;
    const interval = 250;
    // If we time out here, colorization of parameters won't happen until the user changes the source
    // Try this for a maximum of 2 seconds
    const maxWait = 2000;
    const timeout = setInterval(() => {
      waitedSoFar += interval;
      if (waitedSoFar > maxWait) {
        clearInterval(timeout);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((model as any)._tokenization?._tokenizationSupport) {
        clearInterval(timeout);
        updateHighlightsCallback();
      }
    }, interval);

    return () => {
      clearInterval(timeout);
    };
  }, [updateHighlightsCallback, model]);

  return { model, updateHighlights: debouncedUpdateHighlightsCallback };
};

// Add this helper function to register models with cell IDs
export const registerModelWithCellId = (
  model: Editor.ITextModel,
  cellId: CellId,
): void => {
  cellIdsByModelURI[model.uri.toString()] = cellId;
};

const selectNewParams = createShallowResultSelectorV2(
  [
    (state: RootState, hexVersionId: HexVersionId) =>
      hexVersionMPSelectors.getHexVersionSelectors(hexVersionId).select(state)
        .graphV3,
    (state: RootState, hexVersionId: HexVersionId, cellId: CellId) =>
      hexVersionMPSelectors
        .getCellContentSelectors(hexVersionId)
        .selectByCellId(state, cellId),
  ],
  (
    graphV3: boolean,
    contents: CellContentsMP | null | undefined,
  ): readonly (ParameterName | SecretName)[] => {
    if (graphV3 && contents?.__typename === "SqlCell") {
      return mergeStoredCellReferencesV3(
        contents.sqlCellReferencesV3,
        contents.jinjaCellReferencesV3,
      ).newParams.map(({ param }) => param);
    }
    if (
      (contents?.__typename === "CodeCell" ||
        contents?.__typename === "SqlCell" ||
        contents?.__typename === "MarkdownCell") &&
      contents?.cellReferencesV2 != null
    ) {
      return contents.cellReferencesV2.newParams.map(({ param }) => param);
    }

    return stableEmptyArray<ParameterName | SecretName>();
  },
);

const selectCellReferences = createSelectorV2(
  [
    (state: RootState, hexVersionId: HexVersionId) =>
      hexVersionMPSelectors.getHexVersionSelectors(hexVersionId).select(state)
        .graphV3,
    (state: RootState, hexVersionId: HexVersionId, cellId: CellId) =>
      hexVersionMPSelectors
        .getCellContentSelectors(hexVersionId)
        .selectByCellId(state, cellId),
  ],
  (
    graphV3: boolean,
    contents: CellContentsMP | null | undefined,
  ): readonly CellReferencesV2[] => {
    if (graphV3 && contents?.__typename === "SqlCell") {
      const refs = [];
      let sqlRefs = contents.sqlCellReferencesV3;
      let jinjaRefs = contents.jinjaCellReferencesV3;
      if (contents.jinjaSqlReferences != null) {
        [sqlRefs, jinjaRefs] = convertJinjaSqlReferences({
          jinjaSqlReferences: contents.jinjaSqlReferences,
          oldJinjaRefs: contents.jinjaCellReferencesV3,
          parameterOptions: new Map(), // we don't highlight dynamic table references so we can omit these
        });
      }
      if (sqlRefs != null) {
        refs.push(sqlRefs);
      }
      if (jinjaRefs != null) {
        refs.push(jinjaRefs);
      }
      return refs;
    }

    // don't return references if there is a parse error, as the source locations might be out of date
    if (
      contents != null &&
      "cellReferencesParseError" in contents &&
      contents.cellReferencesParseError != null
    ) {
      return stableEmptyArray();
    }

    if (
      contents != null &&
      "cellReferencesV2" in contents &&
      contents.cellReferencesV2 != null
    ) {
      return [contents.cellReferencesV2];
    }

    return stableEmptyArray();
  },
);

const selectRefParams = createShallowResultSelectorV2(
  [
    selectCellReferences,
    (state: RootState, hexVersionId: HexVersionId, cellId: CellId) =>
      selectGraphNodeV3(state, hexVersionId, cellId),
  ],
  (
    cellReferencesArray: readonly CellReferencesV2[],
    graphNode: GraphNodeV3<CellMP> | null,
  ): Set<ParameterName | SecretName> => {
    if (graphNode?.type === "cell") {
      return new Set([
        ...graphNode.inputParams.keys(),
        ...graphNode.outputParams.keys(),
      ]);
    }
    if (!cellReferencesArray) {
      return new Set();
    }
    return new Set(
      cellReferencesArray.flatMap((cellReferences) =>
        [
          ...(cellReferences?.newParams ?? []),
          ...(cellReferences?.referencedParams ?? []),
        ].map((p) => p.param),
      ),
    );
  },
);

const selectRefScopes = createShallowResultSelectorV2(
  [
    selectRefParams,
    (
      state: RootState,
      _hexVersionId: HexVersionId,
      _cellId: CellId,
      appSessionId,
    ) =>
      appSessionMPSelectors
        .getScopeSelectors(appSessionId)
        .selectEntities(state),
  ],
  (
    refParams: Set<ParameterName | SecretName>,
    scope: Record<string, ScopeItemFragment | undefined> | null,
  ): Record<string, ScopeItemFragment | undefined> => {
    if (scope == null) {
      return stableEmptyObject();
    }
    return Object.fromEntries(
      Object.entries(scope).filter(([key]) => refParams.has(key as any)),
    );
  },
);

const selectContext = (
  state: RootState,
  hexVersionId: HexVersionId,
  cellId: CellId,
): HighlightContext => {
  const contents = hexVersionMPSelectors
    .getCellContentSelectors(hexVersionId)
    .selectByCellId(state, cellId);

  if (contents?.__typename === "SqlCell") {
    if (contents.dataFrameCell) {
      return "df_sql";
    }
    return "db_sql";
  }
  return "code";
};
